// The ONE page-header action pattern (right of the page title, in the shell's #page-h-actions slot): a primary .btn.sm (28px) with an optional dropdown of
// items, each with an icon from the central map (ActionIcons). Pages never build their own header-button markup (see pageHeaderActions.test.mjs).
// embedded -> portals into the shell header slot; standalone (legacy non-shell render) -> inline, pushed right.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IcoLn } from './ActionIcons';

// The slot may be absent at first mount (lazy pages): retry across a few animation frames.
export function usePageHeaderSlot(embedded) {
  const [slot, setSlot] = useState(null);
  useEffect(() => {
    if (!embedded) { setSlot(null); return undefined; }
    let cancelled = false, tries = 0;
    const resolve = () => {
      if (cancelled) return;
      const el = document.getElementById('page-h-actions');
      if (el) setSlot(el); else if (tries++ < 60) requestAnimationFrame(resolve);
    };
    resolve();
    return () => { cancelled = true; };
  }, [embedded]);
  return slot;
}

// items: [{ id, label, icon? (ActionIcons name) }]; onPick(id)
export default function PageHeaderActions({ embedded = false, label, items, onPick }) {
  const slot = usePageHeaderSlot(embedded);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false), onKey = e => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const node = <span className="ph-act">
    <button type="button" className="btn primary sm" aria-haspopup="menu" aria-expanded={open} onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>{label} <span aria-hidden="true">▾</span></button>
    {open && <div className="ph-menu" role="menu">
      {items.map(it => <button key={it.id} type="button" role="menuitem" title={it.title} onClick={() => { setOpen(false); onPick(it.id); }}>
        {it.icon && <IcoLn name={it.icon} />}<span>{it.label}</span></button>)}
    </div>}
  </span>;
  if (embedded) return slot ? createPortal(node, slot) : null;
  return <span style={{ marginLeft: 'auto' }}>{node}</span>;
}
